import React, { useEffect, useState } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Tabs, 
  Table, 
  Tag,
  Progress,
  Collapse,
  Spin,
  Alert,
  Button 
} from 'antd';
import {
  DollarOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  DownloadOutlined,
  RiseOutlined,
  FallOutlined
} from '@ant-design/icons';
import { Bar, Pie } from 'react-chartjs-2';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './DashboardPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const { TabPane } = Tabs;
const { Panel } = Collapse;

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://agpipefitting.org.et/api";

const DashboardPage = () => {
  const [stats, setStats] = useState({ 
    totalSales: 0, 
    soldProducts: 0,
    activeCustomers: 0,
    avgOrderValue: 0
  });
  
  const [salesBreakdown, setSalesBreakdown] = useState({ 
    monthly: [], 
    quarterly: [], 
    yearly: [],
    categorical: {},
    geographic: {},
    productType: {}
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Helper functions
  const updateTemporalData = (dataset, key, sale) => {
    if (!dataset[key]) {
      dataset[key] = { totalSales: 0, totalProducts: 0, transactions: 0 };
    }
    dataset[key].totalSales += parseFloat(sale.total_price) || 0;
    dataset[key].totalProducts += parseInt(sale.quantity) || 0;
    dataset[key].transactions++;
  };

  const sortTemporalData = (data, type) => {
    return Object.entries(data).sort(([aKey], [bKey]) => {
      if (type === 'month') return new Date(aKey) - new Date(bKey);
      if (type === 'quarter') {
        const [qA, yA] = aKey.split(' ');
        const [qB, yB] = bKey.split(' ');
        return yA - yB || parseInt(qA.slice(1)) - parseInt(qB.slice(1));
      }
      return parseInt(aKey) - parseInt(bKey);
    });
  };

  const sortByValue = (obj) => {
    return Object.entries(obj)
      .sort(([,a], [,b]) => b - a)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        backgroundColor: '#1A237E',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context) => ` ETB ${context.raw.toLocaleString()}`
        }
      }
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        ticks: { callback: value => `ETB ${value.toLocaleString()}` }
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [salesRes, productsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/sales`),
          axios.get(`${API_BASE_URL}/products`)
        ]);

        const salesData = salesRes.data;
        const products = productsRes.data;

        const breakdowns = calculateBreakdowns(salesData, products);
        const totalSales = salesData.reduce((sum, sale) => sum + (parseFloat(sale.total_price) || 0), 0);
        
        setStats({
          totalSales,
          soldProducts: salesData.reduce((sum, sale) => sum + (parseInt(sale.quantity) || 0), 0),
          activeCustomers: new Set(salesData.map(sale => sale.customer_id)).size,
          avgOrderValue: salesData.length > 0 ? totalSales / salesData.length : 0
        });

        setSalesBreakdown(breakdowns);

      } catch (err) {
        console.error("API Error:", err);
        setError('Failed to load dashboard data. Please check your network connection and API availability.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const calculateBreakdowns = (salesData, products) => {
    const breakdowns = {
      monthly: {},
      quarterly: {},
      yearly: {},
      categorical: {},
      geographic: {},
      productType: {}
    };

    salesData.forEach(sale => {
      const date = new Date(sale.sale_date);
      const product = products.find(p => p.id === sale.product_id);

      // Temporal breakdowns
      const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      const quarterYear = `Q${quarter} ${date.getFullYear()}`;
      
      updateTemporalData(breakdowns.monthly, monthYear, sale);
      updateTemporalData(breakdowns.quarterly, quarterYear, sale);
      updateTemporalData(breakdowns.yearly, date.getFullYear().toString(), sale);

      // Product type breakdown
      if (product) {
        const productType = product.type || product.productType || product.category || 'Unspecified';
        if (!breakdowns.productType[productType]) {
          breakdowns.productType[productType] = {
            totalSales: 0,
            totalProducts: 0,
            transactions: 0
          };
        }
        breakdowns.productType[productType].totalSales += parseFloat(sale.total_price) || 0;
        breakdowns.productType[productType].totalProducts += parseInt(sale.quantity) || 0;
        breakdowns.productType[productType].transactions++;
      }

      // Geographic breakdown
      if (sale.shipping_state) {
        const state = sale.shipping_state.trim();
        breakdowns.geographic[state] = (breakdowns.geographic[state] || 0) + (parseFloat(sale.total_price) || 0);
      }

      // Category breakdown
      if (product?.category) {
        breakdowns.categorical[product.category] = (breakdowns.categorical[product.category] || 0) + (parseFloat(sale.total_price) || 0);
      }
    });

    return {
      monthly: sortTemporalData(breakdowns.monthly, 'month'),
      quarterly: sortTemporalData(breakdowns.quarterly, 'quarter'),
      yearly: sortTemporalData(breakdowns.yearly, 'year'),
      categorical: sortByValue(breakdowns.categorical),
      geographic: sortByValue(breakdowns.geographic),
      productType: Object.entries(breakdowns.productType)
        .sort(([,a], [,b]) => b.totalSales - a.totalSales)
        .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
    };
  };

  const temporalColumns = [
    { title: 'Period', dataIndex: 'period', key: 'period' },
    { 
      title: 'Total Sales', 
      dataIndex: 'totalSales', 
      key: 'totalSales',
      render: (value) => `ETB ${value.toLocaleString()}`,
      align: 'right'
    },
    { 
      title: 'Units Sold', 
      dataIndex: 'unitsSold', 
      key: 'unitsSold',
      align: 'right'
    },
    { 
      title: 'Transactions', 
      dataIndex: 'transactions', 
      key: 'transactions',
      align: 'right'
    },
    { 
      title: 'Avg. Order Value', 
      dataIndex: 'avgOrderValue',
      render: (value) => `ETB ${value.toFixed(2)}`,
      align: 'right'
    }
  ];

  const handleDownloadPDF = () => {
    const element = document.getElementById("dashboard-report");
    const opt = {
      margin: [15, 15],
      filename: `sales-dashboard-${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' }
    };
    html2pdf().set(opt).from(element).save();
  };

  return (
    <div className="dashboard-page" id="dashboard-report">
      <Row justify="space-between" align="middle" className="dashboard-header">
        <Col>
          <h1 className="dashboard-title">Sales Intelligence Dashboard</h1>
          <p className="dashboard-subtitle">AG Pipe Fitting Technologies • Comprehensive Analytics</p>
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
          >
            Export Report
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert 
          message="Data Loading Error"
          description={error}
          type="error" 
          showIcon
          closable
          className="dashboard-alert"
        />
      )}

      {loading ? (
        <div className="loading-container">
          <Spin size="large" tip="Loading Dashboard Data..." />
        </div>
      ) : (
        <>
          <Row gutter={[24, 24]} className="stats-row">
            {/* Statistics Cards */}
          </Row>

          <Tabs defaultActiveKey="1" className="analysis-tabs">
            {/* Temporal Analysis Tab */}
            <TabPane tab="Temporal Analysis" key="1">
              <Collapse accordion className="breakdown-collapse">
                {['monthly', 'quarterly', 'yearly'].map(period => (
                  <Panel 
                    header={`${period.charAt(0).toUpperCase() + period.slice(1)} Breakdown`} 
                    key={period}
                  >
                    <Table 
                      columns={temporalColumns} 
                      dataSource={salesBreakdown[period].map(([period, stats]) => ({
                        key: period,
                        period,
                        totalSales: stats.totalSales,
                        unitsSold: stats.totalProducts,
                        transactions: stats.transactions,
                        avgOrderValue: stats.transactions > 0 ? stats.totalSales / stats.transactions : 0
                      }))}
                      pagination={false}
                      bordered
                    />
                  </Panel>
                ))}
              </Collapse>
            </TabPane>

            {/* Product Type Reports Tab */}
            <TabPane tab="Product Type Analysis" key="4">
              <Row gutter={[24, 24]}>
               
                <Col xs={24} md={12}>
                  <Card className="analysis-card">
                    <h4 className="section-title">Product Type Performance</h4>
                    {Object.keys(salesBreakdown.productType).length > 0 ? (
                      <Table
                        columns={[
                          { 
                            title: 'Product Type', 
                            dataIndex: 'type', 
                            key: 'type',
                            render: (text) => text || 'General Products'
                          },
                          { 
                            title: 'Total Sales', 
                            dataIndex: 'totalSales',
                            render: (v) => `ETB ${(v || 0).toLocaleString()}`,
                            align: 'right'
                          },
                          { 
                            title: 'Units Sold', 
                            dataIndex: 'unitsSold',
                            render: (v) => (v || 0).toLocaleString(),
                            align: 'right'
                          },
                          { 
                            title: 'Transactions', 
                            dataIndex: 'transactions',
                            render: (v) => (v || 0).toLocaleString(),
                            align: 'right'
                          },
                          { 
                            title: 'Avg Price', 
                            render: (_, r) => {
                              const avg = (r.totalSales || 0) / (r.unitsSold || 1);
                              return `ETB ${isNaN(avg) ? '0.00' : avg.toFixed(2)}`;
                            },
                            align: 'right'
                          }
                        ]}
                        dataSource={Object.entries(salesBreakdown.productType).map(([type, stats]) => ({
                          key: type,
                          type,
                          totalSales: stats?.totalSales || 0,
                          unitsSold: stats?.totalProducts || 0,
                          transactions: stats?.transactions || 0
                        }))}
                        pagination={false}
                        bordered
                      />
                    ) : (
                      <Alert
                        message="Product Type Data Loading"
                        description="Ensure products have type information in the database"
                        type="info"
                        showIcon
                      />
                    )}
                  </Card>
                </Col>
              </Row>
            </TabPane>

            {/* Other Tabs */}
          </Tabs>
        </>
      )}
    </div>
  );
};

export default DashboardPage;